import {
  type MediaElement,
  type ResizableElement,
} from '../../types/aha-editor';
import { attributeAsNumberIfExists } from '../../util/media';

type MediaAttributes = Omit<MediaElement, 'type' | 'children'>;
type ResizableAttributes = Omit<ResizableElement, 'type' | 'children'>;

export const getMediaAttributes = (element: Element): MediaAttributes => {
  const src = element.getAttribute('src') ?? '';
  const data: MediaAttributes = {
    media_src: src,
  };

  const position = element.getAttribute('data-position');
  if (position === 'inline') {
    data.media_position = 'inline';
  }

  const alt = element.getAttribute('aria-label');
  if (alt) {
    data.media_alt = alt;
  }

  const borderColor = element.getAttribute('data-border-color');
  if (borderColor) {
    data.media_borderColor = borderColor;
  }

  const borderWidth = element.getAttribute('data-border-width');
  if (borderWidth) {
    const width = parseInt(borderWidth, 10);
    if (!Number.isNaN(width)) {
      data.media_borderWidth = width;
    }
  }

  return data;
};

const getResizableAttributesBase = (
  element: Element,
  widthAttr: string,
  heightAttr: string
): ResizableAttributes => {
  const width = attributeAsNumberIfExists(element, widthAttr, null);
  const height = attributeAsNumberIfExists(element, heightAttr, null);
  const originalWidth = attributeAsNumberIfExists(
    element,
    'data-original-width',
    width
  );
  const originalHeight = attributeAsNumberIfExists(
    element,
    'data-original-height',
    height
  );

  const data: ResizableAttributes = {};

  if (width) {
    data.resizer_width = width;
    data.resizer_originalWidth = originalWidth ?? undefined;
  }

  if (height) {
    data.resizer_height = height;
    data.resizer_originalHeight = originalHeight ?? undefined;
  }

  return data;
};

export const getResizableAttributes = (
  element: Element
): ResizableAttributes => {
  return getResizableAttributesBase(element, 'width', 'height');
};

export const getEmbedResizableAttributes = (
  element: Element
): ResizableAttributes => {
  return getResizableAttributesBase(element, 'data-width', 'data-height');
};

// This are hardcode since when running in faye the Node object is not available
const TEXT_NODE = 3;
const ELEMENT_NODE = 1;

export const isTextNode = (node: Node): node is Text => {
  return node.nodeType === TEXT_NODE;
};

export const isElementNode = (node: Node): node is Element => {
  return node.nodeType === ELEMENT_NODE;
};

export const isHTMLElementNode = (node: Node): node is HTMLElement => {
  if (typeof window === 'undefined') return isElementNode(node);

  return isElementNode(node) && node instanceof HTMLElement;
};

/**
 * Checks if a node has content. For our purposes, a node must either be an element or be a text node with non-whitespace text.
 * Any other `Node` is ignored currently.
 */
export const nodeHasContent = (node: Node): boolean => {
  if (!node) return false;

  switch (node.nodeType) {
    case ELEMENT_NODE:
      return true;

    case TEXT_NODE:
      // Check for any non-whitespace char
      return /\S/.test(node.textContent || '');

    default:
      return false;
  }
};

const CONTAINER_ELEMENTS = ['ul', 'ol', 'dl'];

/**
 * Checks if a node is a container element. Currently this is defined as an element that does not have any content itself, e.g. `<ul>`.
 */
export const isContainerElement = (
  node: Node
): node is HTMLUListElement | HTMLOListElement | HTMLDListElement => {
  if (!node) return false;
  if (node.nodeType !== ELEMENT_NODE) {
    return false;
  }

  const element = node as Element;

  // We only remove empty container elements, other elements are preserved
  return CONTAINER_ELEMENTS.includes(element.tagName.toLowerCase());
};

/**
 * Checks if a node is an empty container element. For example, `<ul>` or `<ol>` that only have whitespace, and no `<li>`/
 */
export const isEmptyContainerNode = (node: Node): boolean => {
  if (!isContainerElement(node)) return false;

  // If there are any children (elements), then let's assume it's not empty
  if (node.children.length > 0) {
    return false;
  }

  return !Array.from(node.childNodes).some((node) => nodeHasContent(node));
};
